/**
 * MobileScreenRouter Component for LOOPINO AI Platform
 * Routes between different mobile screens based on navigation
 */

import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import MobilePostReview from './MobilePostReview';
import PostEditPage from './MobilePostReview/PostEditPage';
import MobileSettings from './MobileSettings';
import MobileCompanyProfile from './MobileCompanyProfile';
import MobileBatteryPanel from './MobileBatteryPanel';
import MobileCalendar from './MobileCalendar';
import MobileAIStorage from './MobileAIStorage';
import ImagePromptEditorPage from './MobilePostReview/ImagePromptEditorPage.jsx';
import MobileStrategy from './MobileStrategy/MobileStrategy.jsx';
import MobileLogin from './MobileLogin';
import MobileSubscriptionPlans from './MobileSubscriptionPlans/MobileSubscriptionPlans.jsx';
import MobileNewCampaignModal from './MobileNewCampaignModal';
import ImageGenerationPage from './MobileImageGenerator/ImageGenerationPage.jsx';
import { MobileSocialHint } from './MobileSocialHint';
import useTestMode from '../../hooks/useTestMode.js';
import useCompany from '../../hooks/useCompany';
import useLanguage from '../../hooks/useLanguage';
import { useCapabilities } from '../../capabilities/CapabilityContext';
import { useMobileAlert } from './common/alerts/MobileAlertContext';
import { hasGeneratedDna } from '../../utils/companyDna';

const formatCoverageLabel = (coverage) => {
  if (!coverage) {
    return null;
  }

  const percent = Number.isFinite(coverage.percent) ? Math.round(coverage.percent) : null;
  if (coverage.status === 'partial' && percent !== null) {
    return `Copertura ${percent}%`;
  }

  if (coverage.status === 'none') {
    return 'Copertura non disponibile';
  }

  return null;
};

const formatFreshnessLabel = (freshness) => {
  if (!freshness?.status) {
    return null;
  }

  if (freshness.status === 'fresh') {
    return 'Freshness verificata dal backend';
  }

  if (freshness.status === 'unknown') {
    return 'Freshness non verificabile';
  }

  return `Freshness: ${freshness.status}`;
};

const CapabilityDegradedState = ({ capability, children }) => {
  const reasonMessage = capability?.entry?.reason?.message || 'Disponibilita parziale dichiarata dal backend.';
  const coverageLabel = formatCoverageLabel(capability?.entry?.coverage);
  const freshnessLabel = formatFreshnessLabel(capability?.entry?.freshness);
  const details = [coverageLabel, freshnessLabel].filter(Boolean).join(' · ');

  return (
    <>
      <section
        aria-live="polite"
        style={{
          margin: '12px 16px 0',
          padding: '12px 14px',
          borderRadius: '16px',
          border: '1px solid rgba(255,255,255,0.12)',
          background: 'rgba(255,255,255,0.06)',
          color: '#f4f6fb'
        }}
      >
        <strong style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Modalita degradata</strong>
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.45 }}>{reasonMessage}</p>
        {details ? (
          <p style={{ margin: '6px 0 0', fontSize: 12, opacity: 0.78 }}>{details}</p>
        ) : null}
      </section>
      {children}
    </>
  );
};

const MobileNewCampaignGuard = ({ onClose, onScreenChange, onCampaignCreated }) => {
  const { currentCompany } = useCompany();
  const { notify } = useMobileAlert();
  const { t } = useLanguage();
  const hasDna = useMemo(() => hasGeneratedDna(currentCompany), [currentCompany]);
  const gateRef = useRef(false);

  useEffect(() => {
    if (!currentCompany) return;
    if (hasDna) return;
    if (gateRef.current) return;
    gateRef.current = true;

    notify({
      variant: 'warning',
      title: t('newCampaign.alerts.dnaRequired', {
        defaultValue: 'Prima di creare una strategia crea il tuo DNA.'
      })
    });

    if (typeof onScreenChange === 'function') {
      onScreenChange('profile');
    } else if (typeof onClose === 'function') {
      onClose();
    }
  }, [currentCompany, hasDna, notify, onClose, onScreenChange, t]);

  if (!currentCompany || !hasDna) {
    return null;
  }

  return (
    <MobileNewCampaignModal
      onClose={onClose}
      onCampaignCreated={onCampaignCreated}
    />
  );
};

const MobileScreenRouter = ({
  currentScreen = 'posts',
  posts,
  targetPostId,
  onPostChange,
  onPostApprove,
  onPostReject,
  onPostRegenerate,
  onEditPost,
  onCampaignClick,
  onThumbnailClick,
  onImageUpload,
  isLoading = false,
  // Post editing props
  editingPost,
  currentPostIndex,
  onCloseEdit,
  onSaveEdit,
  campaignId,
  selectedCampaignIdFromPost,
  // Prompt editor navigation
  onOpenPromptEditor,
  promptEditorImageIndex = 0,
  // Settings props
  onSettingsBack,
  // Profile props
  onProfileBack,
  onProfileSettingsClick,
  onCompanyProfileClick,
  // Battery props
  onBatteryBack,
  onOpenPlans,
  onPlansBack,
  // Calendar props
  onCalendarPostClick,
  calendarTargetDate,
  // AI Storage props
  onAIStorageBack,
  aiStorageView = 'list',
  selectedProjectId = null,
  // Login props
  onLoginSuccess,
  onLoginBack,
  // Logout props
  onLogout,
  // Social Hint props
  onSocialHintBack,
  selectedSocialHint = null,
  socialHintView = 'list',
  // New Campaign props
  onNewCampaignBack,
  onCampaignCreated,
  onNavigateToDna,
  onNavigateToImageGenerator,
  onCreateFirstCampaign,
  // Navigation props
  onScreenChange,
  isPreview = false,
  registerScreenRef,
  onHomeReady
}) => {
  const { isTestModeEnabled } = useTestMode();
  const { getRouteStateForScreen, isAccessible, isReady } = useCapabilities();

  const developmentScreens = ['social-hint'];
  const capabilityState = getRouteStateForScreen(currentScreen);
  const shouldRedirectToPosts = (
    (capabilityState.managed && isReady && !isAccessible(capabilityState))
    || (developmentScreens.includes(currentScreen) && !isTestModeEnabled)
  );

  useEffect(() => {
    if (isPreview) {
      return;
    }

    if (shouldRedirectToPosts && onScreenChange) {
      console.log('[MobileScreenRouter] Redirecting from restricted screen:', currentScreen);
      onScreenChange('posts');
    }
  }, [shouldRedirectToPosts, currentScreen, onScreenChange, isPreview]);

  const handleOpenNewCampaignModal = useCallback(() => {
    if (onScreenChange) {
      onScreenChange('new-campaign');
    }
  }, [onScreenChange]);

  const renderCapabilityManagedScreen = useCallback((screenKey, content) => {
    const screenCapability = getRouteStateForScreen(screenKey);
    if (screenCapability.managed && isReady && !isAccessible(screenCapability)) {
      return null;
    }

    if (screenCapability.state === 'degraded') {
      return (
        <CapabilityDegradedState capability={screenCapability}>
          {content}
        </CapabilityDegradedState>
      );
    }

    return content;
  }, [getRouteStateForScreen, isAccessible, isReady]);

  const renderScreen = (screenKey) => {
    switch (screenKey) {
      case 'posts':
        return (
          <MobilePostReview
            posts={posts}
            targetPostId={targetPostId}
            preferPropPosts={false}
            campaignId={campaignId}
            onPostChange={onPostChange}
            onPostApprove={onPostApprove}
            onPostReject={onPostReject}
            onPostRegenerate={onPostRegenerate}
            onEditPost={onEditPost}
            onCampaignClick={onCampaignClick}
            onThumbnailClick={onThumbnailClick}
            onNavigateToDna={onNavigateToDna}
            onNavigateToImageGenerator={onNavigateToImageGenerator}
            onOpenNewCampaignModal={handleOpenNewCampaignModal}
            onCreateFirstCampaign={onCreateFirstCampaign}
            onImageUpload={onImageUpload}
            isLoading={isLoading}
            onHomeReady={onHomeReady}
          />
        );

      case 'post-edit':
        console.log('[MobileScreenRouter] Rendering PostEditPage with:', {
          editingPost,
          campaignId,
          hasOnCloseEdit: !!onCloseEdit,
          hasOnSaveEdit: !!onSaveEdit
        });
        return (
          <PostEditPage
            post={editingPost}
            onClose={onCloseEdit}
            onSave={onSaveEdit}
            onAIRegenerate={onPostRegenerate}
            onAddImage={onImageUpload}
            campaignId={campaignId}
            isSaving={false}
            onOpenPromptEditor={onOpenPromptEditor}
          />
        );

      case 'prompt-editor':
        return (
          <ImagePromptEditorPage
            post={editingPost}
            imageIndex={promptEditorImageIndex}
            onBack={() => onScreenChange && onScreenChange('post-edit')}
            onRegenerate={(data) => {
              if (!onPostRegenerate) {
                return;
              }

              onPostRegenerate(editingPost, data.type, {
                prompt: data.prompt,
                imageIndex: data.imageIndex,
                payload: data.payload,
                response: data.response,
                source: 'prompt-editor'
              });
            }}
          />
        );

      case 'calendar':
        return (
          <MobileCalendar
            posts={[]}
            onPostClick={onCalendarPostClick}
            targetDate={calendarTargetDate}
            isLoading={isLoading}
          />
        );

      case 'image-generator':
        return renderCapabilityManagedScreen(
          screenKey,
          <ImageGenerationPage
            onBack={() => onScreenChange && onScreenChange('posts')}
          />
        );

      case 'analytics':
        return null;

      case 'aitools':
        return null;

      case 'aistorage':
        return renderCapabilityManagedScreen(
          screenKey,
          <MobileAIStorage
            onBack={onAIStorageBack}
            currentView={aiStorageView}
            selectedProjectId={selectedProjectId}
          />
        );

      case 'strategy':
        return (
          <MobileStrategy
            targetCampaignId={selectedCampaignIdFromPost}
            onScreenChange={onScreenChange}
            onCampaignCreated={onCampaignCreated}
          />
        );

      case 'new-campaign':
        return (
          <MobileNewCampaignGuard
            onClose={onNewCampaignBack}
            onScreenChange={onScreenChange}
            onCampaignCreated={onCampaignCreated}
          />
        );

      case 'settings':
        return (
          <MobileSettings
            onBack={onSettingsBack}
            onProfileClick={onCompanyProfileClick}
            onLogout={onLogout}
          />
        );

      case 'profile':
        return (
          <MobileCompanyProfile
            onBack={onProfileBack}
          />
        );

      case 'battery':
        return (
          <MobileBatteryPanel
            onBack={onBatteryBack}
            onOpenPlans={onOpenPlans}
          />
        );

      case 'subscription-plans':
        return (
          <MobileSubscriptionPlans
            onBack={onPlansBack}
          />
        );

      case 'social-hint':
        if (!isTestModeEnabled) {
          console.warn('[MobileScreenRouter] Unauthorized access to Social Hint, redirecting to posts');
          return renderScreen('posts');
        }
        return (
          <MobileSocialHint
            currentView={socialHintView}
            selectedHintId={selectedSocialHint?.id}
            onBack={onSocialHintBack}
          />
        );

      case 'login':
        return (
          <MobileLogin
            onLoginSuccess={onLoginSuccess}
            onNavigateBack={onLoginBack}
          />
        );

      default:
        console.warn('[MobileScreenRouter] Unknown screen:', screenKey);
        return renderScreen('posts');
    }
  };

  if (isPreview) {
    return (
      <div className="mobile-screen-router-container">
        {renderScreen(currentScreen)}
      </div>
    );
  }

  const renderedScreensRef = useRef(new Set());
  const registerCallbacksRef = useRef(new Map());
  if (!renderedScreensRef.current.has(currentScreen)) {
    renderedScreensRef.current.add(currentScreen);
  }

  const screensToRender = Array.from(renderedScreensRef.current);

  return (
    <div className="mobile-screen-router-container">
      {screensToRender.map((screenKey) => {
        const isActive = screenKey === currentScreen;
        let refCallback = null;
        if (typeof registerScreenRef === 'function') {
          if (!registerCallbacksRef.current.has(screenKey)) {
            registerCallbacksRef.current.set(screenKey, (node) => {
              registerScreenRef(screenKey, node);
              if (!node) {
                registerCallbacksRef.current.delete(screenKey);
              }
            });
          }
          refCallback = registerCallbacksRef.current.get(screenKey);
        }

        const renderedContent = renderScreen(screenKey);
        const content = React.isValidElement(renderedContent)
          ? React.cloneElement(renderedContent, { isActive })
          : renderedContent;

        return (
          <div
            key={screenKey}
            className="mobile-screen-router-layer"
            data-screen={screenKey}
            ref={refCallback}
            style={{
              display: isActive ? 'block' : 'none',
              height: '100%',
              width: '100%'
            }}
            aria-hidden={!isActive}
          >
            {content}
          </div>
        );
      })}
    </div>
  );
};

export default MobileScreenRouter;
